import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { toastApiError } from '../lib/apiToast';

interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  updatedAt: string;
  keycloak_id: string | null;
  phone_e164: string | null;
  preferred_locale: string | null;
  signup_method: string | null;
  email_verified_at: string | null;
}

interface AuditEvent {
  id: string;
  user_id: string | null;
  actor_id: string | null;
  actor_kind: 'user' | 'admin' | 'system' | 'anonymous';
  actor_email: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

interface AuditPage {
  items: AuditEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const ACTION_LABEL: Record<string, { label: string; tone: string }> = {
  'account.created': { label: 'Account created', tone: 'bg-emerald-100 text-emerald-800' },
  'auth.otp.login': { label: 'Signed in (email code)', tone: 'bg-slate-100 text-slate-700' },
  'auth.idp.login': { label: 'Signed in (Google)', tone: 'bg-slate-100 text-slate-700' },
  'auth.password.login': { label: 'Signed in (password)', tone: 'bg-slate-100 text-slate-700' },
  'auth.password.login_failed': { label: 'Failed password login', tone: 'bg-amber-100 text-amber-800' },
  'auth.password.login_blocked_locked': { label: 'Login blocked (locked)', tone: 'bg-red-100 text-red-800' },
  'auth.password.set': { label: 'Password set', tone: 'bg-blue-100 text-blue-800' },
  'auth.password.changed': { label: 'Password changed', tone: 'bg-blue-100 text-blue-800' },
  'auth.password.reset': { label: 'Password reset', tone: 'bg-blue-100 text-blue-800' },
  'document.uploaded': { label: 'Document uploaded', tone: 'bg-indigo-100 text-indigo-800' },
  'document.file_deleted': { label: 'Document file deleted', tone: 'bg-slate-100 text-slate-700' },
  'document.approved': { label: 'Document approved (admin)', tone: 'bg-emerald-100 text-emerald-800' },
  'document.rejected': { label: 'Document rejected (admin)', tone: 'bg-red-100 text-red-800' },
  'job_request.created': { label: 'Job request submitted', tone: 'bg-purple-100 text-purple-800' },
  'job_request.updated': { label: 'Application edited by user', tone: 'bg-purple-100 text-purple-800' },
  'job_request.deleted': { label: 'Job request deleted (admin)', tone: 'bg-red-100 text-red-800' },
  'document.created': { label: 'Document type added', tone: 'bg-indigo-100 text-indigo-800' },
  'document.removed': { label: 'Document type removed', tone: 'bg-slate-100 text-slate-700' },
  'profile.preferences_updated': { label: 'Preferences updated', tone: 'bg-slate-100 text-slate-700' },
  'profile.avatar_uploaded': { label: 'Avatar uploaded', tone: 'bg-slate-100 text-slate-700' },
  'profile.avatar_deleted': { label: 'Avatar deleted', tone: 'bg-slate-100 text-slate-700' },
  'admin.user.deleted': { label: 'User deleted (admin)', tone: 'bg-red-100 text-red-800' },
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const PAGE_SIZE = 50;

// ── Audit metadata rendering ─────────────────────────────────────────
// `job_request.updated` carries `changes` ({ field: { from, to } }) and a
// `documents` reconciliation summary; render those as readable rows and fall
// back to raw JSON for everything else.
type FieldChange = { from: unknown; to: unknown };
type DocumentSummary = { added?: string[]; updated?: string[]; removed?: string[]; kept?: string[] };

const isFieldChange = (v: unknown): v is FieldChange =>
  !!v && typeof v === 'object' && 'from' in v && 'to' in v;

const isChangeMap = (v: unknown): v is Record<string, FieldChange> =>
  !!v && typeof v === 'object' && !Array.isArray(v) && Object.values(v).every(isFieldChange);

const isDocumentSummary = (v: unknown): v is DocumentSummary =>
  !!v && typeof v === 'object' && !Array.isArray(v) &&
  Object.values(v).every((x) => Array.isArray(x) && x.every((y) => typeof y === 'string'));

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x === 'string')) return v.join(', ') || '—';
    // jobs: [{ category, roles }] · documents: [{ kind, … }]
    const parts = v.map((x) => {
      if (x && typeof x === 'object') {
        const o = x as Record<string, unknown>;
        if (typeof o.category === 'string' && Array.isArray(o.roles)) {
          return `${o.category}: ${(o.roles as unknown[]).join(', ')}`;
        }
        if (typeof o.kind === 'string') {
          const extras = Object.entries(o)
            .filter(([k, val]) => k !== 'kind' && val !== undefined && val !== null)
            .map(([k, val]) => `${k}=${Array.isArray(val) ? val.join('/') : String(val)}`);
          return extras.length ? `${o.kind} (${extras.join(', ')})` : o.kind;
        }
      }
      return JSON.stringify(x);
    });
    return parts.join('; ') || '—';
  }
  return JSON.stringify(v);
};

const AuditMetadata: React.FC<{ metadata: Record<string, unknown> }> = ({ metadata }) => {
  const { changes, documents, ...rest } = metadata;
  const changeMap = isChangeMap(changes) ? changes : null;
  const docSummary = isDocumentSummary(documents) ? documents : null;
  const restEntries = Object.entries(rest).filter(([, v]) => v !== undefined);
  const docLines = docSummary
    ? (
        [
          ['added', docSummary.added],
          ['updated', docSummary.updated],
          ['removed', docSummary.removed],
          ['kept (files uploaded or required)', docSummary.kept],
        ] as Array<[string, string[] | undefined]>
      ).filter(([, kinds]) => kinds && kinds.length > 0)
    : [];
  return (
    <div className="mt-1 text-xs text-slate-600 bg-slate-50 rounded p-2 overflow-x-auto space-y-1">
      {changeMap && Object.keys(changeMap).length === 0 && !docLines.length && (
        <div className="text-slate-400">No field changes.</div>
      )}
      {changeMap && Object.keys(changeMap).length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {Object.entries(changeMap).map(([field, change]) => (
            <div key={field} className="contents">
              <dt className="font-mono text-slate-500">{field}</dt>
              <dd className="min-w-0 break-words">
                <span className="line-through text-slate-400">{formatValue(change.from)}</span>
                <span className="mx-1 text-slate-400">→</span>
                <span className="text-slate-800">{formatValue(change.to)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      {docLines.length > 0 && (
        <div>
          <span className="font-mono text-slate-500">documents</span>{' '}
          {docLines.map(([label, kinds]) => `${label}: ${(kinds ?? []).join(', ')}`).join(' · ')}
        </div>
      )}
      {(!changeMap && !docSummary) || restEntries.some(([k]) => k !== 'created') ? (
        <pre className="m-0 whitespace-pre-wrap">
          {JSON.stringify(
            changeMap || docSummary ? Object.fromEntries(restEntries.filter(([k]) => k !== 'created')) : metadata,
            null,
            2,
          )}
        </pre>
      ) : null}
    </div>
  );
};

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { token, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [user, setUser] = useState<AdminUser | null>(null);
  const [audit, setAudit] = useState<AuditPage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (!id) return;
    setLoading(true);
    api.setToken(token);
    const [userRes, auditRes] = await Promise.all([
      api.get<AdminUser>(`/users/${id}`),
      api.get<AuditPage>(`/users/${id}/audit-log?page=${page}&pageSize=${PAGE_SIZE}`),
    ]);
    if (userRes.success && userRes.data) setUser(userRes.data);
    else if (userRes.error?.code === 'FORBIDDEN') setForbidden(true);
    else toastApiError(userRes, 'Failed to load user');

    if (auditRes.success && auditRes.data) setAudit(auditRes.data);
    else if (auditRes.error?.code !== 'FORBIDDEN') toastApiError(auditRes, 'Failed to load audit log');

    setLoading(false);
  }, [id, isAdmin, token, page]);

  useEffect(() => {
    void load();
  }, [load]);

  if (forbidden) return <Forbidden />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/users" className="text-sm text-slate-500 hover:underline">
            ← Back to users
          </Link>
          <h2 className="mt-1 text-lg font-medium text-gray-900">
            {user
              ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email
              : 'User'}
          </h2>
          {user && <p className="text-sm text-slate-500">{user.email}</p>}
        </div>
      </div>

      {user && (
        <div className="bg-white shadow rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Phone</div>
            <div className="text-slate-800">{user.phone_e164 ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Locale</div>
            <div className="text-slate-800">{user.preferred_locale ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Signup</div>
            <div className="text-slate-800">{user.signup_method ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Created</div>
            <div className="text-slate-800">{formatDate(user.createdAt)}</div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Activity history</h3>
          {audit && (
            <span className="text-xs text-slate-500">
              {audit.total} event{audit.total === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {loading && !audit ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
        ) : !audit || audit.items.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">No events recorded.</div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {audit.items.map((ev) => {
              const meta = ACTION_LABEL[ev.action] ?? {
                label: ev.action,
                tone: 'bg-slate-100 text-slate-700',
              };
              const hasMeta = ev.metadata && Object.keys(ev.metadata).length > 0;
              return (
                <li key={ev.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-xs text-slate-400 font-mono">{ev.action}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3">
                        <span>
                          actor: {ev.actor_kind}
                          {ev.actor_email ? ` (${ev.actor_email})` : ''}
                        </span>
                        {ev.entity && (
                          <span>
                            entity: {ev.entity}
                            {ev.entity_id ? ` #${ev.entity_id.slice(0, 8)}` : ''}
                          </span>
                        )}
                        {ev.ip && <span>ip: {ev.ip}</span>}
                      </div>
                      {hasMeta && <AuditMetadata metadata={ev.metadata} />}
                    </div>
                    <time className="text-xs text-slate-500 whitespace-nowrap pt-1">
                      {formatDate(ev.created_at)}
                    </time>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {audit && audit.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-500">
              Page {page} of {audit.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(audit.totalPages, p + 1))}
              disabled={page >= audit.totalPages}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
